import { useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  useTheme,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import { useAppDispatch } from 'store';
import {
  fetchFunnelResultReasons,
  FunnelResultReason,
  moveOpportunityToLoss,
  MoveOpportunityToLossPayload,
} from 'store/reducers/funnels';
import emptyStringToNull from 'utils/emptyStringToNull';

const validationSchema = yup.object({
  resultReason: yup.object().nullable(),
});

type FormValues = {
  resultReason: FunnelResultReason | null;
};

interface DeleteOpportunityConfirmProps {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  opportunityIds: Array<string>;
  onSuccess: () => void;
}

function DeleteOpportunityConfirm(props: DeleteOpportunityConfirmProps) {
  const { open, onClose, funnelId, opportunityIds, onSuccess } = props;
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      dispatch(fetchFunnelResultReasons());
    }
  }, [dispatch, open]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      resultReason: null,
    },
    validationSchema,
    onSubmit: async (values: FormValues) => {
      const payload: MoveOpportunityToLossPayload = {
        funnelId,
        opportunityIds,
        lossResultReasonId: emptyStringToNull(values.resultReason?.id),
      };

      await dispatch(moveOpportunityToLoss(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Operação realizada com sucesso.');
          onSuccess();
          onClose();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const handleClose = () => {
    if (!formik.isSubmitting) onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onExited: () => {
          formik.resetForm();
        },
      }}
    >
      <DialogHeader title="Remover oportunidade" onClose={handleClose} />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning">
                Tem certeza que deseja remover os leads selecionados? Esta ação
                é permanente e não poderá ser desfeita.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              {opportunityIds.length > 1 && (
                <Alert severity="warning" icon={false}>
                  Você está{' '}
                  <Box component="strong">
                    deletando {opportunityIds.length} oportunidade
                    {opportunityIds.length === 1 ? '' : 's'}.
                  </Box>
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            style={{ backgroundColor: theme.palette.error.main }}
            disabled={formik.isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
          >
            Remover definitivamente
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

export default DeleteOpportunityConfirm;
